// Draw:给国名,凭记忆在空白画布上画出轮廓,提交后与真实形状比对打分。
// 方案见 docs/geodle-draw-mode-plan.md
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import '../styles/draw.css';
import { useI18n } from '../i18n';
import { localName, flagSrc, type Country } from '../lib/countries';
import { answerFor } from '../lib/daily';
import { todayKey } from '../lib/game';
import { getModeState, setModeState, recordWin } from '../lib/storage';
import { getCountryRings } from '../lib/outline';
import { celebrate } from '../lib/confetti';
import {
  scoreDrawing, secondsFor, rankIndexFor,
  type DrawResult, type Ring, type Stroke, type Pt,
} from '../lib/draw-score';
import { Link, buildCountryPath } from '../router';
import { poolFor } from '../lib/daily';

/** 画布逻辑坐标边长(与显示尺寸无关,保证不同屏幕评分一致) */
const SIZE = 430;
/** 达到此分数即算「今日完成」并计入连胜 */
const PASS = 1;

type Phase = 'ready' | 'drawing' | 'result';

interface Saved {
  score: number;
  strokes: Stroke[];
}
const sKey = (day: string) => `geodle:draw:${day}`;
function loadSaved(day: string): Saved | null {
  try {
    const v = localStorage.getItem(sKey(day));
    return v ? (JSON.parse(v) as Saved) : null;
  } catch {
    return null;
  }
}

interface EndlessSave {
  solved: number;
  streak: number;
  best: number;
}
const eKey = 'geodle:endless:draw';
function loadEndless(): EndlessSave {
  try {
    const v = localStorage.getItem(eKey);
    return v ? (JSON.parse(v) as EndlessSave) : { solved: 0, streak: 0, best: 0 };
  } catch {
    return { solved: 0, streak: 0, best: 0 };
  }
}
function saveEndless(s: EndlessSave): void {
  try {
    localStorage.setItem(eKey, JSON.stringify(s));
    window.dispatchEvent(new Event('geodle:endless'));
  } catch {
    /* ignore */
  }
}

export default function DrawMode({ endless = false }: { endless?: boolean }) {
  const { t, lang } = useI18n();
  const td = t.draw;
  const day = todayKey();
  const pool = useMemo(() => poolFor('draw'), []);

  const [answer, setAnswer] = useState<Country>(() =>
    endless ? pool[Math.floor(Math.random() * pool.length)] : answerFor('draw', day),
  );
  const [rings, setRings] = useState<Ring[] | null>(null);
  const [phase, setPhase] = useState<Phase>('ready');
  const [result, setResult] = useState<DrawResult | null>(null);
  const [left, setLeft] = useState(0);
  const [total, setTotal] = useState(30);
  const [canUndo, setCanUndo] = useState(false);

  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const strokes = useRef<Stroke[]>([]);
  const drawingRef = useRef(false);

  // 载入目标轮廓(与 Outline 模式同一套几何)
  useEffect(() => {
    let on = true;
    setRings(null);
    getCountryRings(answer.ccn3, SIZE, SIZE, 26).then((r) => {
      if (!on || !r) return;
      setRings(r);
      setTotal(secondsFor(r, SIZE));
    });
    return () => {
      on = false;
    };
  }, [answer.ccn3]);

  // Daily:已完成则直接恢复结果
  useEffect(() => {
    if (endless || !rings) return;
    if (getModeState('draw', day).status !== 'won') return;
    const saved = loadSaved(day);
    if (!saved) return;
    strokes.current = saved.strokes;
    setResult(scoreDrawing(saved.strokes, rings));
    setPhase('result');
  }, [endless, rings, day]);

  const ctxOf = useCallback((cv: HTMLCanvasElement) => {
    const r = cv.getBoundingClientRect();
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    cv.width = Math.max(1, r.width * dpr);
    cv.height = Math.max(1, r.height * dpr);
    const c = cv.getContext('2d')!;
    c.setTransform((dpr * r.width) / SIZE, 0, 0, (dpr * r.height) / SIZE, 0, 0);
    return c;
  }, []);

  const paintStrokes = useCallback(() => {
    const cv = canvasRef.current;
    if (!cv) return;
    const c = ctxOf(cv);
    c.clearRect(0, 0, SIZE, SIZE);
    c.strokeStyle = '#2e2412';
    c.lineWidth = 2.3;
    c.lineJoin = 'round';
    c.lineCap = 'round';
    for (const s of strokes.current) {
      if (s.length < 2) {
        if (s.length) {
          c.beginPath();
          c.arc(s[0][0], s[0][1], 1.3, 0, Math.PI * 2);
          c.fillStyle = '#2e2412';
          c.fill();
        }
        continue;
      }
      c.beginPath();
      c.moveTo(s[0][0], s[0][1]);
      for (let i = 1; i < s.length; i++) c.lineTo(s[i][0], s[i][1]);
      c.stroke();
    }
  }, [ctxOf]);

  const paintResult = useCallback(
    (res: DrawResult) => {
      const cv = canvasRef.current;
      if (!cv || !rings) return;
      const c = ctxOf(cv);
      c.clearRect(0, 0, SIZE, SIZE);
      // 真轮廓
      c.strokeStyle = '#bcae8e';
      c.lineWidth = 1.5;
      c.fillStyle = 'rgba(200,180,130,.18)';
      for (const r of rings) {
        c.beginPath();
        c.moveTo(r[0][0], r[0][1]);
        for (let i = 1; i < r.length; i++) c.lineTo(r[i][0], r[i][1]);
        c.closePath();
        c.fill();
        c.stroke();
      }
      // 偏差射线
      c.lineWidth = 0.8;
      for (const ray of res.rays) {
        if (ray.d < 3) continue;
        c.strokeStyle = ray.out ? 'rgba(181,70,47,.5)' : 'rgba(63,106,158,.5)';
        c.beginPath();
        c.moveTo(ray.p[0], ray.p[1]);
        c.lineTo(ray.q[0], ray.q[1]);
        c.stroke();
      }
      // 你的线
      c.strokeStyle = '#2e2412';
      c.lineWidth = 2.1;
      c.lineJoin = 'round';
      c.lineCap = 'round';
      for (const s of res.fitted) {
        if (s.length < 2) continue;
        c.beginPath();
        c.moveTo(s[0][0], s[0][1]);
        for (let i = 1; i < s.length; i++) c.lineTo(s[i][0], s[i][1]);
        c.stroke();
      }
    },
    [ctxOf, rings],
  );

  const finish = useCallback(() => {
    if (!rings || !drawingRef.current) return;
    drawingRef.current = false;
    const res = scoreDrawing(strokes.current, rings);
    setResult(res);
    setPhase('result');
    if (res.score >= 75) celebrate();

    if (!endless) {
      const done = res.score >= PASS;
      setModeState('draw', day, { guesses: [String(res.score)], status: done ? 'won' : 'lost' });
      try {
        localStorage.setItem(sKey(day), JSON.stringify({ score: res.score, strokes: strokes.current }));
      } catch {
        /* ignore */
      }
      if (done) recordWin(day);
      window.dispatchEvent(new Event('geodle:progress'));
    } else {
      const e = loadEndless();
      saveEndless({
        solved: e.solved + 1,
        streak: res.score >= PASS ? e.streak + 1 : 0,
        best: Math.max(e.best, res.score),
      });
    }
  }, [rings, endless, day]);

  // 倒计时
  useEffect(() => {
    if (phase !== 'drawing') return;
    if (left <= 0) {
      finish();
      return;
    }
    const id = setTimeout(() => setLeft((v) => v - 1), 1000);
    return () => clearTimeout(id);
  }, [phase, left, finish]);

  // 结果画布重绘
  useEffect(() => {
    if (phase === 'result' && result) requestAnimationFrame(() => paintResult(result));
  }, [phase, result, paintResult]);

  // 绘制交互
  useEffect(() => {
    const cv = canvasRef.current;
    if (!cv || phase !== 'drawing') return;
    const toLocal = (e: PointerEvent): Pt => {
      const r = cv.getBoundingClientRect();
      return [((e.clientX - r.left) * SIZE) / r.width, ((e.clientY - r.top) * SIZE) / r.height];
    };
    const down = (e: PointerEvent) => {
      if (!drawingRef.current) return;
      try {
        cv.setPointerCapture(e.pointerId);
      } catch {
        /* ignore */
      }
      strokes.current.push([toLocal(e)]);
      setCanUndo(true);
      paintStrokes();
    };
    const move = (e: PointerEvent) => {
      if (!drawingRef.current || !strokes.current.length) return;
      if (e.pointerType === 'mouse' && !e.buttons) return;
      const s = strokes.current[strokes.current.length - 1];
      const p = toLocal(e);
      const last = s[s.length - 1];
      if (!last || Math.hypot(p[0] - last[0], p[1] - last[1]) > 1.2) {
        s.push(p);
        paintStrokes();
      }
    };
    cv.addEventListener('pointerdown', down);
    cv.addEventListener('pointermove', move);
    return () => {
      cv.removeEventListener('pointerdown', down);
      cv.removeEventListener('pointermove', move);
    };
  }, [phase, paintStrokes]);

  function start() {
    strokes.current = [];
    setCanUndo(false);
    setResult(null);
    drawingRef.current = true;
    setLeft(total);
    setPhase('drawing');
    requestAnimationFrame(paintStrokes);
  }
  function next() {
    setAnswer(pool[Math.floor(Math.random() * pool.length)]);
    strokes.current = [];
    setResult(null);
    setPhase('ready');
  }

  const name = localName(answer, lang);
  const rank = result ? td.ranks[rankIndexFor(result.score)] : '';
  const warn = phase === 'drawing' && left <= 5;

  return (
    <div className="draw">
      {phase !== 'result' ? (
        <div className="draw-ask">
          <span className="k">{phase === 'ready' ? td.askLabel : td.drawingLabel}</span>
          <h2 className="n">{name}</h2>
          {phase === 'ready' && <p className="h">{td.askHint}</p>}
        </div>
      ) : (
        <p className="draw-verdict">{rank}</p>
      )}

      <div className={`draw-board${phase === 'drawing' ? ' live' : ''}`}>
        {phase === 'ready' ? (
          <div className="draw-blank">
            <span aria-hidden="true">?</span>
            <em>{td.blank}</em>
            <button className="conn-btn primary" onClick={start} disabled={!rings}>
              {td.start}
            </button>
          </div>
        ) : (
          <canvas ref={canvasRef} aria-label={td.canvasLabel} />
        )}
      </div>

      {phase === 'drawing' && (
        <div className={`draw-timer${warn ? ' warn' : ''}`}>
          <div className="bar">
            <div className="fill" style={{ width: `${(left / total) * 100}%` }} />
          </div>
          <span className="num">{left}s</span>
        </div>
      )}

      {phase === 'result' && result && (
        <>
          <div className="draw-legend">
            <span>
              <i style={{ borderColor: '#bcae8e' }} />
              {td.legendTrue}
            </span>
            <span>
              <span className="dot" />
              {td.legendYours}
            </span>
            <span>
              <i style={{ borderColor: 'var(--accent)' }} />
              {td.legendOut}
            </span>
            <span>
              <i style={{ borderColor: '#3f6a9e' }} />
              {td.legendIn}
            </span>
          </div>
          <div className="draw-stats">
            <div className="draw-stat lead">
              <div className="k">{td.score}</div>
              <div className="v">
                {result.score}
                <small> / 100</small>
              </div>
            </div>
            <div className="draw-stat">
              <div className="k">{td.accuracy}</div>
              <div className="v">
                {result.accuracy}
                <small>%</small>
              </div>
            </div>
            <div className="draw-stat">
              <div className="k">{td.coverage}</div>
              <div className="v">
                {result.coverage}
                <small>%</small>
              </div>
            </div>
            <div className="draw-stat">
              <div className="k">{td.deviation}</div>
              <div className="v">
                {result.deviation}
                <small>%</small>
              </div>
            </div>
          </div>
          <div className="draw-learn">
            <img src={flagSrc(answer)} alt="" width={42} height={28} />
            <div className="t">
              <b>{name}</b>
              <span>
                {answer.capital} · {answer.region}
              </span>
            </div>
            <Link className="conn-btn" to={buildCountryPath(lang, answer.cca3)}>
              {td.learnMore} →
            </Link>
          </div>
        </>
      )}

      <div className="draw-acts">
        {phase === 'drawing' && (
          <>
            <button
              className="conn-btn"
              disabled={!canUndo}
              onClick={() => {
                strokes.current.pop();
                setCanUndo(strokes.current.length > 0);
                paintStrokes();
              }}
            >
              {td.undo}
            </button>
            <button
              className="conn-btn"
              disabled={!canUndo}
              onClick={() => {
                strokes.current = [];
                setCanUndo(false);
                paintStrokes();
              }}
            >
              {td.clear}
            </button>
            <button className="conn-btn primary" onClick={finish}>
              {td.done}
            </button>
          </>
        )}
        {phase === 'result' && endless && (
          <button className="conn-btn primary" onClick={next}>
            {td.next} →
          </button>
        )}
      </div>
    </div>
  );
}
